import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { getTranslation, getTranslationArr, logError } from "../helpers/utils";

export const getLaws = createAsyncThunk(
  "OffenceSlice/getLaws",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/laws`
      );
      return {
        res: response.data.data["Relevant Laws"],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OffenceSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

export const getSelectedLaw = createAsyncThunk(
  "OffenceSlice/getSelectedLaw",
  async (data, { rejectWithValue, getState }) => {
    try {
      // Guard against undefined id
      if (!data.id) {
        console.warn('[getSelectedLaw] Missing id:', data);
        return { res: [], langId: data.langId };
      }
      
      // Check cache first
      const state = getState();
      const cacheKey = `law_${data.id}`;
      const cached = state.offence.lawsCache?.[cacheKey];
      if (cached) {
        if (process.env.NODE_ENV === 'development') console.log('[OffenceSlice] Using cached law for:', data.id);
        return { res: cached, langId: data.langId, cacheKey, fromCache: true };
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/laws/${data.id}`
      );
      return {
        res: response.data.data["Relevant Law"],
        langId: data.langId,
        cacheKey,
      };
    } catch (error) {
      logError(error, "OffenceSlice");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getGuidance = createAsyncThunk(
  "OffenceSlice/getGuidance",
  async (data, { rejectWithValue, getState }) => {
    try {
      // Check cache first
      const state = getState();
      const cacheKey = `guidance_${data.district}`;
      const cached = state.offence.guidanceCache?.[cacheKey];
      if (cached) {
        if (process.env.NODE_ENV === 'development') console.log('[OffenceSlice] Using cached guidance for:', data.district);
        return { res: cached, langId: data.langId, cacheKey, fromCache: true };
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/guidance-step/${data.district}`
      );
      return {
        res: response.data.data["Guidance Step"],
        langId: data.langId,
        cacheKey,
      };
    } catch (error) {
      logError(error, "OffenceSlice");
      return rejectWithValue(error.response.data);
    }
  }
);

export const getSelectedGuidance = createAsyncThunk(
  "OffenceSlice/getSelectedGuidance",
  async (data, { rejectWithValue }) => {
    try {
      // Guard against undefined id or district
      if (!data.id || !data.district) {
        console.warn('[getSelectedGuidance] Missing id or district:', data);
        return { res: [], langId: data.langId };
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_LOCAL_API}/one-guidance-step/${data.id}/${data.district}`
      );
      return {
        res: response.data.data["Guidance Step"],
        langId: data.langId,
      };
    } catch (error) {
      logError(error, "OffenceSlice");
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const OffenceSlice = createSlice({
  name: "offence",
  initialState: {
    offenceLaws: [],
    offenceGuidance: [],
    selectedLaw: null,
    selectedGuide: null,
    lawsCache: {},      // Cache laws per offence ID
    guidanceCache: {},  // Cache guidance per district ID
  },
  reducers: {
    setSelectedLaw: (state, action) => {
      state.selectedLaw = action.payload;
    },
    setSelectedGuide: (state, action) => {
      state.selectedGuide = action.payload;
    },
    resetOffStates: (state, _action) => {
      state.offenceLaws = [];
      state.offenceGuidance = [];
      state.selectedLaw = null;
      state.selectedGuide = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLaws.fulfilled, (state, action) => {
        state.offenceLaws = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          return trans;
        });
      })
      .addCase(getLaws.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getSelectedLaw.fulfilled, (state, action) => {
        const raw = action.payload.res;
        // Normalize: backend may return array or single object; cache stores as-is
        if (!action.payload.fromCache && action.payload.cacheKey) {
          state.lawsCache[action.payload.cacheKey] = raw;
        }
        const list = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
        const law = list.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj?.title, action.payload.langId);
          trans.description = getTranslation(
            obj?.description,
            action.payload.langId
          );
          trans.laws = obj?.laws?.map((lawItem) => {
            let transLaw = { ...lawItem };
            transLaw.title = getTranslation(lawItem?.title, action.payload.langId);
            return transLaw;
          }) || [];
          trans.sections = getTranslationArr(obj?.sections, action.payload.langId);
          return trans;
        });
        state.selectedLaw = law[0] ?? null;
      })
      .addCase(getSelectedLaw.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getGuidance.fulfilled, (state, action) => {
        // Cache the raw response
        if (!action.payload.fromCache && action.payload.cacheKey) {
          state.guidanceCache[action.payload.cacheKey] = action.payload.res;
        }
        
        state.offenceGuidance = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          return trans;
        });
      })
      .addCase(getGuidance.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(getSelectedGuidance.fulfilled, (state, action) => {
        if (!action.payload.res || action.payload.res.length === 0) {
          console.warn('[getSelectedGuidance] No data returned');
          state.selectedGuide = null;
          return;
        }
        
        const guides = action.payload.res.map((obj) => {
          let trans = { ...obj };
          trans.title = getTranslation(obj.title, action.payload.langId);
          trans.description = getTranslation(
            obj.description,
            action.payload.langId
          );
          trans.guidance_steps = (obj.guidance_steps || []).map((step) => {
            let newStep = { ...step };
            newStep.title = getTranslation(step.title, action.payload.langId);
            // Preserve video_url field
            newStep.video_url = step.video_url;
            newStep.guidance_step_description =
              (step.guidance_step_description || []).map((stepDes) => {
                let desObj = { ...stepDes };
                desObj.description = getTranslation(
                  stepDes.description,
                  action.payload.langId
                );
                desObj.heading = getTranslation(
                  stepDes.heading,
                  action.payload.langId
                );
                return desObj;
              });
            return newStep;
          });
          return trans;
        });
        state.selectedGuide = guides[0] || null;
      })
      .addCase(getSelectedGuidance.rejected, (state, action) => {
        toast.error(action.payload);
      });
  },
});
export const { setSelectedLaw, setSelectedGuide, resetOffStates } =
  OffenceSlice.actions;

export default OffenceSlice.reducer;
